import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import axios from 'axios';
import {
  Building2, MapPin, Users, Sparkles, Search, ChevronRight,
  Filter, X, Loader2, Globe, SlidersHorizontal
} from 'lucide-react';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VENUE_TYPE_LABELS = {
  event_space: 'Event Space', restaurant: 'Restaurant / Bar', rooftop: 'Rooftop',
  warehouse: 'Warehouse / Industrial', gallery: 'Gallery / Studio', outdoor: 'Outdoor / Garden',
  hotel: 'Hotel / Ballroom', private_estate: 'Private Estate', brewery: 'Brewery / Winery',
  coworking: 'Coworking / Meeting Room',
};

function Breadcrumbs({ state, stateName, city, category, categoryName }) {
  const crumbs = [{ label: 'All Venues', to: '/venues' }];
  if (state) crumbs.push({ label: stateName || state.toUpperCase(), to: `/venues/${state}` });
  if (city) crumbs.push({ label: city.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()), to: `/venues/${state}/${city}` });
  if (category) crumbs.push({ label: categoryName || category, to: `/venues/${state}/${city}/${category}` });

  return (
    <nav className="flex items-center gap-1 text-xs text-muted-foreground mb-4 flex-wrap" data-testid="venue-breadcrumbs">
      {crumbs.map((c, i) => (
        <React.Fragment key={c.to}>
          {i > 0 && <ChevronRight className="h-3 w-3 text-muted-foreground" />}
          {i < crumbs.length - 1 ? (
            <Link to={c.to} className="hover:text-indigo-400 transition-colors">{c.label}</Link>
          ) : (
            <span className="text-foreground font-medium">{c.label}</span>
          )}
        </React.Fragment>
      ))}
    </nav>
  );
}

function VenueCard({ venue, seoBase, hasCategory }) {
  // Always include category in the URL for proper routing to VenueDetailPage
  const categorySlug = (venue.venue_type || 'event-space').replace(/_/g, '-');
  const venueUrl = hasCategory && seoBase
    ? `${seoBase}/${venue.slug}`
    : `/venues/${(venue.state || 'co').toLowerCase()}/${(venue.city || 'denver').toLowerCase().replace(/\s+/g, '-')}/${categorySlug}/${venue.slug}`;

  return (
    <Link to={venueUrl} data-testid={`venue-card-${venue.slug}`}>
      <Card className="bg-muted/60 border-border hover:border-indigo-500/30 transition-all group overflow-hidden h-full">
        <div className="aspect-[16/10] bg-muted overflow-hidden">
          {venue.cover_photo || venue.photos?.[0] ? (
            <img
              src={venue.cover_photo || venue.photos[0]}
              alt={venue.name}
              loading="lazy"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Building2 className="h-10 w-10 text-muted-foreground" />
            </div>
          )}
        </div>
        <CardContent className="p-4">
          <div className="flex items-center gap-1.5 mb-1.5">
            <Badge className="bg-indigo-500/20 text-indigo-400 text-[8px]">
              {VENUE_TYPE_LABELS[venue.venue_type] || venue.venue_type}
            </Badge>
            {venue.is_featured && (
              <Badge className="bg-amber-500/20 text-amber-400 text-[8px]">Featured</Badge>
            )}
          </div>
          <h3 className="text-sm font-bold text-foreground group-hover:text-indigo-400 transition-colors line-clamp-1">
            {venue.name}
          </h3>
          <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
            <MapPin className="h-3 w-3" />{venue.city}, {venue.state}
          </p>
          <div className="flex items-center gap-3 mt-2.5 text-[10px] text-muted-foreground">
            <span className="flex items-center gap-0.5"><Users className="h-3 w-3" /> Up to {venue.capacity_max}</span>
            {venue.space_count > 0 && <span>{venue.space_count} space{venue.space_count > 1 ? 's' : ''}</span>}
            {venue.package_count > 0 && (
              <span className="flex items-center gap-0.5 text-amber-400">
                <Sparkles className="h-3 w-3" /> {venue.package_count} pkg{venue.package_count > 1 ? 's' : ''}
              </span>
            )}
          </div>
          {venue.amenities?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {venue.amenities.slice(0, 4).map(a => (
                <span key={a} className="px-1.5 py-0.5 rounded text-[8px] bg-muted text-muted-foreground">{a}</span>
              ))}
              {venue.amenities.length > 4 && (
                <span className="px-1.5 py-0.5 rounded text-[8px] bg-muted text-muted-foreground">+{venue.amenities.length - 4}</span>
              )}
            </div>
          )}
          <div className="flex items-center justify-between mt-3 pt-2.5 border-t border-border">
            {venue.starting_price > 0 ? (
              <p className="text-sm font-bold text-foreground">From ${venue.starting_price}<span className="text-[10px] text-muted-foreground">/hr</span></p>
            ) : (
              <p className="text-xs text-muted-foreground">Contact for pricing</p>
            )}
            <span className="text-[10px] text-indigo-400 font-medium group-hover:underline">View Details</span>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

function AmenityFilter({ amenities, selected, onToggle }) {
  if (!amenities || amenities.length === 0) return null;
  return (
    <div className="mb-6" data-testid="amenity-filter">
      <div className="flex items-center gap-2 mb-2">
        <SlidersHorizontal className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-xs font-semibold text-muted-foreground">Filter by Amenities</span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {amenities.map(a => (
          <button
            key={a.name}
            onClick={() => onToggle(a.name)}
            className={`px-2.5 py-1 rounded-full text-[10px] font-medium transition-all ${
              selected.includes(a.name)
                ? 'bg-indigo-500 text-foreground'
                : 'bg-muted text-muted-foreground hover:bg-accent'
            }`}
            data-testid={`amenity-${a.name.toLowerCase().replace(/\s+/g, '-')}`}
          >
            {a.name} ({a.count})
          </button>
        ))}
      </div>
    </div>
  );
}

export default function VenuesMarketplace() {
  const { state, city, category, venueSlug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [meta, setMeta] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAmenities, setSelectedAmenities] = useState([]);
  const [states, setStates] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  // If we're on a single venue page, redirect to the detail page
  // This is handled by the router now — this component handles listings

  const fetchVenues = useCallback(async () => {
    setLoading(true);
    try {
      let url;
      const params = {};
      if (selectedAmenities.length > 0) {
        params.amenity = selectedAmenities[0]; // Backend supports one at a time, filter client-side for multi
      }

      if (state && city && category) {
        url = `${API}/venues/seo/${state}/${city}/${category}`;
      } else if (state && city) {
        url = `${API}/venues/seo/${state}/${city}`;
      } else if (state) {
        url = `${API}/venues/seo/${state}`;
      } else {
        url = `${API}/venues/browse`;
      }

      const res = await axios.get(url, { params });
      let venuesList = res.data.venues || [];

      // Client-side multi-amenity filtering
      if (selectedAmenities.length > 1) {
        venuesList = venuesList.filter(v =>
          selectedAmenities.every(a => v.amenities?.some(va => va.toLowerCase().includes(a.toLowerCase())))
        );
      }

      // Client-side search
      if (searchTerm) {
        const q = searchTerm.toLowerCase();
        venuesList = venuesList.filter(v =>
          v.name?.toLowerCase().includes(q) ||
          v.city?.toLowerCase().includes(q) ||
          v.description?.toLowerCase().includes(q)
        );
      }

      setVenues(venuesList);
      setTotal(res.data.total || venuesList.length);
      setMeta(res.data);
    } catch (err) {
      console.error('Failed to load venues:', err);
      setVenues([]);
    } finally {
      setLoading(false);
    }
  }, [state, city, category, selectedAmenities, searchTerm]);

  useEffect(() => { fetchVenues(); }, [fetchVenues]);

  // Fetch states for national page
  useEffect(() => {
    if (!state) {
      axios.get(`${API}/venues/seo/states`).then(r => setStates(r.data.states || [])).catch(() => {});
    }
  }, [state]);

  const toggleAmenity = (name) => {
    setSelectedAmenities(prev =>
      prev.includes(name) ? prev.filter(a => a !== name) : [...prev, name]
    );
  };

  const seoBase = state && city && category
    ? `/venues/${state}/${city}/${category}`
    : state && city
    ? `/venues/${state}/${city}`
    : state
    ? `/venues/${state}`
    : '';

  // Page title and description
  const pageTitle = category
    ? `${meta.category_name || category} in ${meta.city || city}, ${meta.state_name || state}`
    : city
    ? `Venue Rental Partners in ${meta.city || city}, ${meta.state_name || state}`
    : state
    ? `Venue Rental Partners in ${meta.state_name || state}`
    : 'Event Venues & Rental Spaces — Find Wedding, Corporate & Party Venues';

  const pageDescription = category
    ? `Browse ${total} ${meta.category_name || category} venues in ${meta.city || city}, ${meta.state_name || state}`
    : city
    ? `Discover ${total} venue rental partners in ${meta.city || city}, ${meta.state_name || state}`
    : state
    ? `Explore venue rental partners across ${meta.state_name || state}`
    : `Discover premium event venues across Denver, Las Vegas, Los Angeles, Charlotte and beyond. Wedding venues, corporate spaces, rooftops, lofts and warehouses.`;

  return (
    <div className="min-h-screen bg-background pt-20 pb-12 px-4" data-testid="venues-marketplace">
      <SEO
        title={`${pageTitle} | Eventism`}
        description={`${pageDescription}. Compare capacity, amenities, photos and pricing. Book your perfect event space with Eventism.`}
        keywords={[
          pageTitle,
          city ? `${city} venue rental` : '',
          city ? `${city} event space` : '',
          city ? `wedding venue ${city}` : '',
          city ? `corporate venue ${city}` : '',
          state ? `${state} event venues` : '',
          category ? `${category} venue` : '',
          'venue rental marketplace',
          'event spaces',
          'Eventism venues',
        ].filter(Boolean)}
      />
      {/* Schema.org JSON-LD: BreadcrumbList */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Venue Rental Partners", "item": `${window.location.origin}/venues` },
          ...(state ? [{ "@type": "ListItem", "position": 2, "name": meta.state_name || state.toUpperCase(), "item": `${window.location.origin}/venues/${state}` }] : []),
          ...(city ? [{ "@type": "ListItem", "position": 3, "name": (meta.city || city).replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()), "item": `${window.location.origin}/venues/${state}/${city}` }] : []),
          ...(category ? [{ "@type": "ListItem", "position": 4, "name": meta.category_name || category.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) }] : [])
        ]
      }) }} />
      {/* Schema.org JSON-LD: ItemList of venues */}
      {venues.length > 0 && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "name": pageTitle,
          "description": pageDescription,
          "numberOfItems": venues.length,
          "itemListElement": venues.slice(0, 30).map((v, i) => ({
            "@type": "ListItem",
            "position": i + 1,
            "item": {
              "@type": "EventVenue",
              "name": v.name,
              "description": v.description || '',
              "address": {
                "@type": "PostalAddress",
                "addressLocality": v.city,
                "addressRegion": v.state,
                "addressCountry": "US"
              },
              ...(v.cover_photo && { "image": v.cover_photo }),
              ...(v.capacity_max && { "maximumAttendeeCapacity": v.capacity_max }),
              "url": `${window.location.origin}/venues/${(v.state || 'co').toLowerCase()}/${(v.city || 'denver').toLowerCase().replace(/\s+/g, '-')}/${(v.venue_type || 'event-space').replace(/_/g, '-')}/${v.slug}`
            }
          }))
        }) }} />
      )}
      <div className="max-w-6xl mx-auto">
        <Breadcrumbs
          state={state}
          stateName={meta.state_name}
          city={city}
          category={category}
          categoryName={meta.category_name}
        />

        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-1">
            <Globe className="h-5 w-5 text-indigo-400" />
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground" data-testid="venues-page-title">
              {pageTitle}
            </h1>
          </div>
          <p className="text-sm text-muted-foreground">{pageDescription}</p>
        </div>

        {/* Search + Filter Bar */}
        <div className="flex items-center gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search venues..."
              className="pl-9 bg-card border-border h-9"
              data-testid="venue-search-input"
            />
          </div>
          <Button
            size="sm"
            variant={showFilters ? 'default' : 'outline'}
            className={showFilters ? 'bg-indigo-600' : 'border-border text-muted-foreground'}
            onClick={() => setShowFilters(!showFilters)}
            data-testid="toggle-filters-btn"
          >
            <Filter className="h-3.5 w-3.5 mr-1" />
            Filters {selectedAmenities.length > 0 && `(${selectedAmenities.length})`}
          </Button>
        </div>

        {/* Active Filters */}
        {selectedAmenities.length > 0 && (
          <div className="flex items-center gap-1.5 mb-4 flex-wrap" data-testid="active-filters">
            {selectedAmenities.map(a => (
              <Badge key={a} className="bg-indigo-500/20 text-indigo-400 text-[10px] cursor-pointer hover:bg-indigo-500/30" onClick={() => toggleAmenity(a)}>
                {a} <X className="h-2.5 w-2.5 ml-1" />
              </Badge>
            ))}
            <button onClick={() => setSelectedAmenities([])} className="text-[10px] text-muted-foreground hover:text-foreground ml-1">
              Clear all
            </button>
          </div>
        )}

        {/* Amenity Filter Panel */}
        {showFilters && meta.amenities && (
          <AmenityFilter amenities={meta.amenities} selected={selectedAmenities} onToggle={toggleAmenity} />
        )}

        {/* National Page: State Cards */}
        {!state && !loading && states.length > 0 && (
          <div className="mb-8">
            <h2 className="text-base font-semibold text-foreground mb-3 flex items-center gap-2">
              <MapPin className="h-4 w-4 text-indigo-400" /> Browse by State
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
              {states.map(s => (
                <Link key={s.abbreviation} to={`/venues/${s.abbreviation}`} data-testid={`state-card-${s.abbreviation}`}>
                  <Card className="bg-muted/60 border-border hover:border-indigo-500/30 transition-all group">
                    <CardContent className="p-3 text-center">
                      <p className="text-sm font-semibold text-foreground group-hover:text-indigo-400">{s.abbreviation.toUpperCase()}</p>
                      <p className="text-[10px] text-muted-foreground">{s.name}</p>
                      <p className="text-[9px] text-indigo-400 mt-1">{s.venue_count} venue{s.venue_count > 1 ? 's' : ''}</p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* State Page: City Cards */}
        {state && !city && meta.cities?.length > 0 && (
          <div className="mb-8">
            <h2 className="text-base font-semibold text-foreground mb-3 flex items-center gap-2">
              <MapPin className="h-4 w-4 text-indigo-400" /> Cities in {meta.state_name || state.toUpperCase()}
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
              {meta.cities.map(c => (
                <Link key={c.slug} to={`/venues/${state}/${c.slug}`} data-testid={`city-card-${c.slug}`}>
                  <Card className="bg-muted/60 border-border hover:border-indigo-500/30 transition-all group">
                    <CardContent className="p-3 text-center">
                      <p className="text-sm font-semibold text-foreground group-hover:text-indigo-400">{c.name}</p>
                      <p className="text-[9px] text-indigo-400 mt-1">{c.count} venue{c.count > 1 ? 's' : ''}</p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* City Page: Category Cards */}
        {state && city && !category && meta.categories?.length > 0 && (
          <div className="mb-8">
            <h2 className="text-base font-semibold text-foreground mb-3 flex items-center gap-2">
              <Building2 className="h-4 w-4 text-indigo-400" /> Categories in {meta.city}
            </h2>
            <div className="flex flex-wrap gap-2">
              {meta.categories.map(c => (
                <Link key={c.id} to={`/venues/${state}/${city}/${c.id.replace(/_/g, '-')}`} data-testid={`category-card-${c.id}`}>
                  <Badge className="bg-muted hover:bg-accent text-muted-foreground text-xs px-3 py-1.5 cursor-pointer transition-colors">
                    {c.name} ({c.count})
                  </Badge>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Venue Grid */}
        {loading ? (
          <div className="text-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-indigo-400 mx-auto" />
          </div>
        ) : venues.length > 0 ? (
          <>
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs text-muted-foreground">{venues.length} venue{venues.length !== 1 ? 's' : ''} found</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="venue-grid">
              {venues.map(v => (
                <VenueCard key={v.venue_id} venue={v} seoBase={seoBase} hasCategory={!!category} />
              ))}
            </div>
          </>
        ) : (
          <div className="text-center py-16">
            <Building2 className="h-14 w-14 text-muted-foreground mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-foreground">No venues found</h3>
            <p className="text-muted-foreground text-sm mt-1">
              {selectedAmenities.length > 0 ? 'Try removing some filters' : 'Venue partners coming soon to this area!'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
